import { FormikProps } from "formik"
import React, { FC, memo } from "react"
import css from "../../styles/FormStyles.module.css"
import { RegistrationPanelValidationType } from "./RegistrationPanelValidationSchema"
import { Gender, GetCompanyQuery } from "../../generated/graphql"
import { Button, Checkbox, Form, Input, Radio } from "antd"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import { isDefined } from "../../helpers/typeScriptHelpers"
import moment from "moment"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { LinkOutlined } from "@ant-design/icons"
import { blue, red } from "@ant-design/colors"
import SepaData from "./SepaData"
import BirthdayInput from "../../views/BirthdayInput/BirthdayInput"
import { useTranslation } from "react-i18next"
import { URL_AGB, URL_DATENSCHUTZ } from "../../models/url"
import { isEmbeddedForInternalRoutes } from "../../helpers/embedMode"

type RegistrationPanelStep1Props = {
    formikProps: FormikProps<RegistrationPanelValidationType>
    checkIfExists: (field: string, value: string, shouldValidate: boolean) => void
    companySettings?: GetCompanyQuery["companyPublic"]
    useCase?: "registration" | "account"
}

const RegistrationPanelStep1: FC<RegistrationPanelStep1Props> = ({ formikProps, checkIfExists, companySettings, useCase }) => {
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const { t } = useTranslation()
    const { values } = formikProps
    const { handleBlur, handleTextInputChange, validateStatus, help, handleCheckboxChange, safeSetFieldValue } = createInputHelpers(formikProps)

    const handleDateInput = (field: string, e: { target: { value: string } }) => {
        if (!formikProps.values) return
        formikProps.setFieldTouched("birthday", true)
        safeSetFieldValue(field, e.target.value ? moment(e.target.value, "YYYY-MM-DD").toISOString() : null, true)
    }

    const AGBLink = ({ label, icon, style }: { label?: string | React.ReactNode; icon?: boolean; style?: React.CSSProperties }) => {
        // Prüfe ob externe URL vorhanden ist
        const externalUrl = companySettings?.agb
        // Prüfe ob App in iFrame läuft (für interne Route)
        const isInIframe = isEmbeddedForInternalRoutes()

        // Wenn keine externe URL vorhanden ist und App in iFrame läuft, verwende interne Route
        if (!externalUrl && isInIframe) {
            return (
                <Button style={{ paddingLeft: 0, fontWeight: 500, ...style }} type='link' size='small'>
                    <a href={`#${URL_AGB}`}>
                        {label ?? "AGB"}
                        {icon && <LinkOutlined style={{ color: blue[5], marginLeft: 3, fontSize: "small", ...style }} />}
                    </a>
                </Button>
            )
        }

        // Externe URL hat Priorität
        if (externalUrl) {
            return (
                <Button style={{ paddingLeft: 0, fontWeight: 500, ...style }} type='link' size='small'>
                    <a href={externalUrl} target='_blank' rel='noopener noreferrer'>
                        {label ?? "AGB"}
                        {icon && <LinkOutlined style={{ color: blue[5], marginLeft: 3, fontSize: "small", ...style }} />}
                    </a>
                </Button>
            )
        }

        // Fallback: Zeige nur Text ohne Link
        return <span>{label ?? "AGB"}</span>
    }

    const PrivacyLink = ({ label, icon, style }: { label?: string | React.ReactNode; icon?: boolean; style?: React.CSSProperties }) => {
        // Prüfe ob externe URL vorhanden ist
        const externalUrl = companySettings?.privacyStatement
        // Prüfe ob App in iFrame läuft (für interne Route)
        const isInIframe = isEmbeddedForInternalRoutes()

        // Wenn keine externe URL vorhanden ist und App in iFrame läuft, verwende interne Route
        if (!externalUrl && isInIframe) {
            return (
                <Button style={{ paddingLeft: 0, fontWeight: 500, ...style }} type='link' size='small'>
                    <a href={`#${URL_DATENSCHUTZ}`}>
                        {label ?? "Datenschutzerklärung"}
                        {icon && <LinkOutlined style={{ color: blue[5], marginLeft: 3, fontSize: "small", ...style }} />}
                    </a>
                </Button>
            )
        }

        // Externe URL hat Priorität
        if (externalUrl) {
            return (
                <Button style={{ paddingLeft: 0, fontWeight: 500, ...style }} type='link' size='small'>
                    <a href={externalUrl} target='_blank' rel='noopener noreferrer'>
                        {label ?? "Datenschutzerklärung"}
                        {icon && <LinkOutlined style={{ color: blue[5], marginLeft: 3, fontSize: "small", ...style }} />}
                    </a>
                </Button>
            )
        }

        // Fallback: Zeige nur Text ohne Link
        return <span>{label ?? "Datenschutzerklärung"}</span>
    }

    return (
        <>
            <Form.Item
                label={media !== "xs" ? t("registration.contactDataForm.salutation.label", "Anrede") : ""}
                // name='gender'
                // valuePropName='value'
                required
            >
                <Radio.Group value={(values?.gender as Gender) ?? Gender.NotDefined} onChange={(e) => safeSetFieldValue("gender", e.target.value)}>
                    <Radio value={Gender.Female}>{t("registration.contactDataForm.salutation.options.mrs", "Frau")}</Radio>
                    <Radio value={Gender.Male}>{t("registration.contactDataForm.salutation.options.mr", "Herr")}</Radio>
                    <Radio value={Gender.Diverse}>{t("registration.contactDataForm.salutation.options.other", "Divers")}</Radio>
                    <Radio value={Gender.NotDefined}>{t("registration.contactDataForm.salutation.options.none", "Keine Angabe")}</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item
                colon={false}
                label={t("registration.contactDataForm.firstName.label", "Vorname")}
                validateStatus={validateStatus("firstname")}
                help={useCase === "account" ? t("registration.contactDataForm.firstName.help.accountDisabled", "Der Vorname kann nicht geändert werden") : help("firstname")}
                // help={formikProps.errors.firstname ? String(formikProps.errors.firstname) : undefined}
                // hasFeedback={formikProps.errors.firstname ? true : false}
                required
                validateTrigger='onBlur'
                // validateTrigger='onChange'
                // validateTrigger='onKeyUp'
            >
                <Input
                    data-testid='registration-firstname-input'
                    value={values?.firstname}
                    placeholder={t("registration.contactDataForm.firstName.placeholder", "Vorname")}
                    name='firstname'
                    onChange={handleTextInputChange("firstname")}
                    onBlur={handleBlur("firstname")}
                    status={formikProps.errors.firstname ? "error" : undefined}
                    disabled={useCase === "account"}
                />
            </Form.Item>
            <Form.Item
                colon={false}
                label={t("registration.contactDataForm.lastName.label", "Nachname")}
                validateStatus={validateStatus("lastname")}
                help={useCase === "account" ? t("registration.contactDataForm.lastName.help.accountDisabled", "Der Nachname kann nicht geändert werden") : help("lastname")}
                // help={formikProps.errors.lastname ? String(formikProps.errors.lastname) : undefined}
                // hasFeedback={formikProps.errors.lastname ? true : false}
                required
                validateTrigger='blur'
            >
                <Input
                    data-testid='registration-lastname-input'
                    value={values?.lastname}
                    placeholder={t("registration.contactDataForm.lastName.placeholder", "Nachname")}
                    name='lastname'
                    onChange={handleTextInputChange("lastname")}
                    onBlur={handleBlur("lastname")}
                    status={formikProps.errors.lastname ? "error" : undefined}
                    disabled={useCase === "account"}
                />
            </Form.Item>
            <Form.Item
                className={css.formItem}
                label={t("registration.contactDataForm.birthDate.label", "Geburtsdatum")}
                validateStatus={validateStatus("birthday")}
                help={help("birthday")}
                // help={formikProps.errors.birthday ? String(formikProps.errors.birthday) : undefined}
                // hasFeedback={formikProps.errors.birthday ? true : false}
                required
            >
                <BirthdayInput
                    data-testid='birthday'
                    onChange={(e) => handleDateInput("birthday", e)}
                    placeholder={t("registration.contactDataForm.birthDate.placeholder", "TT.MM.JJJJ")}
                    value={isDefined(values?.birthday) ? moment(values?.birthday).format("YYYY-MM-DD") : ""}
                    status={formikProps.errors.birthday ? "error" : undefined}
                />
            </Form.Item>
            <Form.Item
                colon={false}
                label={t("registration.contactDataForm.streetAndNumber.label", "Straße, Hausnummer")}
                validateStatus={validateStatus("street")}
                help={help("street")}
                // help={formikProps.errors.street ? String(formikProps.errors.street) : undefined}
                // hasFeedback={formikProps.errors.street ? true : false}
                required
                validateTrigger='blur'
            >
                <Input
                    data-testid='registration-street-input'
                    value={values?.street ?? ""}
                    placeholder={t("registration.contactDataForm.streetAndNumber.placeholder", "Straße, Hausnummer")}
                    name='street'
                    onChange={handleTextInputChange("street")}
                    onBlur={handleBlur("street")}
                    status={formikProps.errors.street ? "error" : undefined}
                />
            </Form.Item>
            <Input.Group compact>
                <Form.Item
                    colon={false}
                    label={t("registration.contactDataForm.zipCode.label", "PLZ")}
                    validateStatus={validateStatus("postCode")}
                    help={help("postCode")}
                    // help={formikProps.errors.postCode ? String(formikProps.errors.postCode) : undefined}
                    // hasFeedback={formikProps.errors.postCode ? true : false}
                    required
                    validateTrigger='blur'
                    style={{ width: "30%", marginBottom: 0 }}
                >
                    <Input
                        data-testid='registration-postcode-input'
                        value={values?.postCode ?? ""}
                        placeholder={t("registration.contactDataForm.zipCode.placeholder", "PLZ")}
                        name='postCode'
                        onChange={handleTextInputChange("postCode")}
                        onBlur={handleBlur("postCode")}
                        status={formikProps.errors.postCode ? "error" : undefined}
                    />
                </Form.Item>
                <Form.Item
                    colon={false}
                    label={t("registration.contactDataForm.city.label", "Ort")}
                    validateStatus={validateStatus("city")}
                    help={help("city")}
                    // help={formikProps.errors.city ? String(formikProps.errors.city) : undefined}
                    // hasFeedback={formikProps.errors.city ? true : false}
                    required
                    validateTrigger='blur'
                    style={{ width: "70%", marginBottom: 0 }}
                >
                    <Input
                        data-testid='registration-city-input'
                        value={values?.city ?? ""}
                        placeholder={t("registration.contactDataForm.city.placeholder", "Ort")}
                        name='city'
                        onChange={handleTextInputChange("city")}
                        onBlur={handleBlur("city")}
                        status={formikProps.errors.city ? "error" : undefined}
                    />
                </Form.Item>
            </Input.Group>
            <Form.Item
                colon={false}
                label={t("registration.contactDataForm.mobilePhone.label", "Handy Nr.")}
                validateStatus={validateStatus("mobileNumber")}
                help={help("mobileNumber")}
                // help={formikProps.errors.mobileNumber ? String(formikProps.errors.mobileNumber) : undefined}
                // hasFeedback={formikProps.errors.mobileNumber ? true : false}
                required
                validateTrigger='blur'
            >
                <Input
                    data-testid='registration-mobile-input'
                    value={values?.mobileNumber ?? ""}
                    placeholder={t("registration.contactDataForm.mobilePhone.placeholder", "Handy Nr.")}
                    name='mobileNumber'
                    onChange={handleTextInputChange("mobileNumber")}
                    onBlur={handleBlur("mobileNumber")}
                    status={formikProps.errors.mobileNumber ? "error" : undefined}
                />
            </Form.Item>
            <Form.Item
                colon={false}
                label={t("registration.contactDataForm.phone.label", "Festnetz")}
                validateStatus={validateStatus("phoneNumber")}
                help={help("phoneNumber")}
                // help={formikProps.errors.phoneNumber ? String(formikProps.errors.phoneNumber) : undefined}
                // hasFeedback={formikProps.errors.phoneNumber ? true : false}
                validateTrigger='blur'
            >
                <Input
                    value={values?.phoneNumber ?? ""}
                    placeholder={t("registration.contactDataForm.phone.placeholder", "Festnetz")}
                    name='phoneNumber'
                    onChange={handleTextInputChange("phoneNumber")}
                    onBlur={handleBlur("phoneNumber")}
                    status={formikProps.errors.phoneNumber ? "error" : undefined}
                />
            </Form.Item>
            {/* <Form.Item validateStatus={validateStatus("dsgvo")} help={help("dsgvo")} required>
                <Checkbox name='dsgvo' checked={values?.dsgvo} onChange={handleCheckboxChange("dsgvo")}>
                    AGB gelesen und akzeptiert
                </Checkbox>
            </Form.Item> */}
            {/* <Button>Lastschriftdaten hinterlegen</Button> */}
            <SepaData formikProps={formikProps} useCase={useCase} />
            <Form.Item validateStatus={validateStatus("dsgvo")} help={formikProps.errors.dsgvo ? String(formikProps.errors.dsgvo) : undefined} required>
                <Checkbox data-testid='registration-agb-checkbox' className={css.inputLabel} name='dsgvo' checked={values?.dsgvo} onChange={handleCheckboxChange("dsgvo")}>
                    <div style={{ color: formikProps.errors.dsgvo ? red[5] : undefined }}>
                        <span>{t("registration.contactDataForm.agb.label", "Ich habe die")} </span>
                        <AGBLink label={t("registration.contactDataForm.agb.linkAgb", "AGB")} style={{ color: formikProps.errors.dsgvo ? red[5] : undefined }} />
                        <span> {t("registration.contactDataForm.agb.and", "und die")} </span>
                        <PrivacyLink label={t("registration.contactDataForm.agb.linkPrivacy", "Datenschutzerklärung")} style={{ color: formikProps.errors.dsgvo ? red[5] : undefined }} />
                        <span> {t("registration.contactDataForm.agb.accepted", "gelesen und akzeptiert")}</span>
                    </div>
                </Checkbox>
            </Form.Item>
        </>
    )
}

export default memo(RegistrationPanelStep1)
